import {
  faArrowsRotate,
  faCircleQuestion,
  faPartyHorn,
  faPinata,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from '@uidotdev/usehooks';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
} from 'nuqs';
import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { invalidateArea } from '@/api/places';
import { TripProvider, useTripContext } from '@/contexts/TripContext';
import { BrandWordmark } from '@/design-system/composite/BrandWordmark';
import { CategoryCustomizationModal } from '@/design-system/composite/CategoryCustomizationModal';
import { CollaborateModal } from '@/design-system/composite/CollaborateModal';
import {
  type CreateCustomPinDraft,
  CreateCustomPinModal,
} from '@/design-system/composite/CreateCustomPinModal';
import { FiltersPopover } from '@/design-system/composite/FiltersPopover';
import { InstallAppModal } from '@/design-system/composite/InstallAppModal';
import { ItineraryList } from '@/design-system/composite/ItineraryList';
import { ItineraryMap } from '@/design-system/composite/ItineraryMap';
import { MapPlaceholder } from '@/design-system/composite/MapPlaceholder';
import { ModeBubble } from '@/design-system/composite/ModeBubble';
import { PlaceDetailPanel } from '@/design-system/composite/PlaceDetailPanel';
import { RefreshConfirmationModal } from '@/design-system/composite/RefreshConfirmationModal';
import { ScoutedList } from '@/design-system/composite/ScoutedList';
import { SearchBar } from '@/design-system/composite/SearchBar';
import { TripHeader } from '@/design-system/composite/TripHeader';
import { TripModeMenu } from '@/design-system/composite/TripModeMenu';
import { TripPanel, TripPlanningView } from '@/design-system/composite/TripPanel';
import { TutorialModal } from '@/design-system/composite/TutorialModal';
import { UserMenu } from '@/design-system/composite/UserMenu';
import { fireConfetti } from '@/design-system/primitives/Confetti';
import { IconButton } from '@/design-system/primitives/IconButton';
import { PanelCollapseHandle } from '@/design-system/primitives/PanelCollapseHandle';
import { SlideOverSheet } from '@/design-system/primitives/SlideOverSheet';
import { Tabs } from '@/design-system/primitives/Tabs';
import { useCategoryGroups } from '@/hooks/useCategoryGroups';
import { matchesExclusion, useCustomExclusions } from '@/hooks/useCustomExclusions';
import { useEnabledSubcategories } from '@/hooks/useEnabledSubcategories';
import { usePlacesSearch } from '@/hooks/usePlacesSearch';
import { signOut, useSession } from '@/lib/authClient';
import type { AppCategory } from '@/types/itinerary';
import type { Trip } from '@/types/trip';
import { twx } from '@/utils/tailwind';

const tripModes = ['explore', 'scout', 'curate', 'travel'] as const;
const tripModeParser = parseAsStringLiteral(tripModes);

const modeLabels: Record<(typeof tripModes)[number], string> = {
  explore: 'Exploring',
  scout: 'Scouting',
  curate: 'Curating',
  travel: 'Traveling',
};

/** Per-mode accent used by the mode-switch progress bar. */
const modeAccent: Record<(typeof tripModes)[number], string> = {
  explore: 'var(--color-primary-emphasized)',
  scout: 'var(--color-secondary-emphasized)',
  curate: 'var(--color-tertiary-emphasized)',
  travel: 'var(--color-quaternary-emphasized)',
};

/** Top-of-viewport progress bar shown while a mode-switch transition is
 *  pending. Critically driven by React's `isPending` from useTransition
 *  rather than a useEffect-after-mode-change — that ensures the bar
 *  becomes visible on the very first frame the user clicks a mode, BEFORE
 *  the heavy mount/unmount work runs. A previous version waited for the
 *  mode useEffect to fire, which is queued behind the slow commit and so
 *  the bar only appeared AFTER the freeze had already ended. The shine
 *  gradient slides continuously so the bar reads as "still working"
 *  regardless of how long the new tree takes to render. */
const ModeSwitchProgress = ({
  mode,
  isPending,
}: {
  mode: (typeof tripModes)[number];
  isPending: boolean;
}) => (
  <div
    aria-hidden
    className={twx(
      'fixed inset-x-0 top-0 z-toast pointer-events-none transition-opacity duration-150 motion-reduce:hidden',
      isPending ? 'opacity-100' : 'opacity-0',
    )}
  >
    {/* 4px-tall bar with a sliding "shine" — uses a gradient with a
        bright accent band; the keyframe slides its background-position
        across the element so the highlight moves continuously. A subtle
        accent glow underneath adds visibility against the header. */}
    <div
      className="h-1 motion-safe:animate-kawaii-mode-progress"
      style={{
        backgroundImage: `linear-gradient(90deg, transparent 0%, ${modeAccent[mode]} 50%, transparent 100%)`,
        backgroundSize: '50% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundColor: `color-mix(in oklab, ${modeAccent[mode]} 35%, transparent)`,
        boxShadow: `0 2px 6px color-mix(in oklab, ${modeAccent[mode]} 55%, transparent)`,
      }}
    />
  </div>
);

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

const AppInner = () => {
  const {
    trips,
    isPanelOpen,
    closePanel,
    selectedTripId,
    updateTripLocation,
    importLiveSyncedTrip,
    selectTrip,
    openPanel,
    addPlace,
  } = useTripContext();

  const isAdmin = useSession().data?.user.role === 'admin';

  const [lat, setLat] = useQueryState('lat', parseAsFloat);
  const [lng, setLng] = useQueryState('lng', parseAsFloat);
  const [radius, setRadius] = useQueryState('radius', parseAsInteger.withDefault(10));
  const [selectedItemId, setSelectedItemId] = useQueryState('item', parseAsString);
  const [selectedCategories, setSelectedCategoriesState] = useQueryState(
    'categories',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  // Cleared when transitioning into Explore mode so the search bar resets.
  const [, setLocationName] = useQueryState('q', parseAsString);
  const [liveJoinId, setLiveJoinId] = useQueryState('live', parseAsString);
  const [mode, setMode] = useQueryState('mode', tripModeParser.withDefault('explore'));
  // Scout-mode small-screen bottom-panel tab. Persisted in URL so the
  // user's choice survives reload + back/forward.
  const [scoutTab, setScoutTab] = useQueryState(
    'scout-tab',
    parseAsStringLiteral(['places', 'scouted'] as const).withDefault('places'),
  );
  const [activeDay, setActiveDay] = useQueryState('day', parseAsInteger);
  const [showRoutes, setShowRoutes] = useQueryState('routes', parseAsBoolean.withDefault(true));

  const scoutMode = mode === 'scout';
  const curateMode = mode === 'curate';
  const travelMode = mode === 'travel';
  // Filter row (radius/categories) makes sense wherever the user can refine a
  // place search — Explore and Scout. Curate and Travel work off saved trip
  // places, not the live search.
  const showFilterRow = mode === 'explore' || scoutMode;

  // First-place celebration: useTrips dispatches `trippy-first-place-
  // added` when the user adds the very first place to a brand-new trip
  // (gated by per-trip localStorage flag so it fires exactly once). We
  // listen here so any add path — heart click, drag-drop, etc. — gets
  // the moment without each add site needing to know.
  useEffect(() => {
    const onFirstPlace = () =>
      fireConfetti({ count: 30, emitFromBottom: window.innerHeight * 0.3 });
    window.addEventListener('trippy-first-place-added', onFirstPlace);
    return () => window.removeEventListener('trippy-first-place-added', onFirstPlace);
  }, []);

  // One-time URL migration for legacy `?mode=` values. Both `plan` (the
  // original name) and `shortlist` (the previous rename) now map to `curate`.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('mode');
    if (raw === 'plan' || raw === 'shortlist') void setMode('curate');
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMode]);

  // Clear the place-detail selection whenever the mode changes. Skip the first
  // mount so a URL with both ?mode= and ?item= can deep-link without us
  // wiping the item selection immediately.
  const isFirstModeRender = useRef(true);
  useEffect(() => {
    // `mode` is the trigger — referenced via void so the linter keeps it in deps.
    void mode;
    if (isFirstModeRender.current) {
      isFirstModeRender.current = false;
      return;
    }
    void setSelectedItemId(null);
  }, [mode, setSelectedItemId]);

  // When transitioning from any other mode into Explore, reset the search
  // surface so the user lands on a clean "where to next?" empty state — the
  // search bar text, current location, category filters, and place selection
  // all clear. First-mount is skipped via the prevModeRef so deep links
  // (e.g., `?mode=explore&lat=…&lng=…`) survive.
  const prevModeRef = useRef(mode);
  useEffect(() => {
    const prev = prevModeRef.current;
    prevModeRef.current = mode;
    if (mode === 'explore' && prev !== 'explore') {
      void setLocationName(null);
      void setLat(null);
      void setLng(null);
      void setSelectedCategoriesState(null);
    }
  }, [mode, setLocationName, setLat, setLng, setSelectedCategoriesState]);

  // Category-customization plumbing. Selection is FSQ-only — passed to the
  // worker so it knows which supplemental subcategories to fetch on each
  // search. When `isReady` is false (groups still loading), we pass `null`
  // and let the worker use its server-side defaults for the first call.
  const categoryGroups = useCategoryGroups();
  const selection = useEnabledSubcategories(categoryGroups.groups);
  const customExclusions = useCustomExclusions();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
  // Custom-pin draft — non-null while the create-custom-pin modal is
  // open. Carries the lat/lng of the map click that triggered it so
  // the modal can submit a pin at exactly that location.
  const [customPinDraft, setCustomPinDraft] = useState<CreateCustomPinDraft | null>(null);
  // Add-to-Home-Screen iOS instructions modal. Opened from the trip
  // selector menu on iOS Safari; non-iOS browsers fire the native
  // install prompt inline (see useInstallPrompt) and don't open this.
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const handleCloseInstallModal = useCallback(() => setIsInstallModalOpen(false), []);
  const handleOpenInstallInstructions = useCallback(() => setIsInstallModalOpen(true), []);
  // The Collaborate modal lives here at the App level — it used to live inside
  // TripSelectorContent but the popover's mousedown outside-click handler would
  // unmount it on first click before the modal's own click handler could run.
  // Trip-selector buttons dispatch a window event to open it.
  const [collaborateForTripId, setCollaborateForTripId] = useState<string | null>(null);
  useEffect(() => {
    const onCollaborate = (e: Event) => {
      const detail = (e as CustomEvent<{ tripId?: string }>).detail;
      if (detail?.tripId) setCollaborateForTripId(detail.tripId);
    };
    window.addEventListener('trippy-open-collaborate-modal', onCollaborate);
    return () => {
      window.removeEventListener('trippy-open-collaborate-modal', onCollaborate);
    };
  }, []);
  // Bumping the nonce after an invalidate-area call forces usePlacesSearch's
  // effect to refire and pick up the now-empty cache.
  const [refreshNonce, setRefreshNonce] = useState(0);

  const supplementalCategoryIdsForSearch = useMemo(
    () => (selection.isReady ? [...selection.enabled] : null),
    [selection.isReady, selection.enabled],
  );
  // Rapid toggles in the customization modal would otherwise fire one search
  // per click. Debounce so a burst of toggles coalesces into a single search.
  const debouncedSupplementalIds = useDebounce(supplementalCategoryIdsForSearch, 400);

  const { items, isLoading, isSettling } = usePlacesSearch({
    lat,
    lng,
    radiusMiles: radius,
    supplementalCategoryIds: debouncedSupplementalIds,
    refreshNonce,
  });

  const handleRefreshArea = useCallback(async () => {
    if (lat === null || lng === null) return;
    await invalidateArea({
      lat,
      lng,
      radiusMeters: Math.round(radius * 1609.344),
    });
    setRefreshNonce((n) => n + 1);
  }, [lat, lng, radius]);

  // User-driven mode changes go through useTransition so React can keep
  // the OLD UI fully interactive (and the loading bar visible) while the
  // new mode's heavy subtree mounts at lower priority. Without this the
  // mode-change render blocks the main thread for hundreds of ms — long
  // enough that the progress bar's own setState wouldn't commit until
  // after the freeze had ended.
  //
  // The `useEffect`-driven `setMode` calls below (trip-coupling, legacy
  // URL migration) keep using the raw setter so they don't gate on a
  // user interaction — they're already non-urgent in the React sense.
  const [isModeSwitchPending, startModeTransition] = useTransition();
  const handleModeChange = useCallback(
    (m: (typeof tripModes)[number]) =>
      startModeTransition(() => {
        void setMode(m);
      }),
    [setMode],
  );
  const handleShowRoutesChange = useCallback(
    (s: boolean) => void setShowRoutes(s),
    [setShowRoutes],
  );
  const clearSelectedItem = useCallback(() => void setSelectedItemId(null), [setSelectedItemId]);
  const handleSearchBarLocation = useCallback(
    (name: string, tripLat: number, tripLng: number) => {
      if (selectedTripId) updateTripLocation(selectedTripId, name, tripLat, tripLng);
    },
    [selectedTripId, updateTripLocation],
  );
  const handleOpenCategoryCustomization = useCallback(() => setIsCategoryModalOpen(true), []);
  const handleCloseCategoryModal = useCallback(() => setIsCategoryModalOpen(false), []);
  const handleCloseRefreshModal = useCallback(() => setIsRefreshModalOpen(false), []);
  const handleOpenCurate = useCallback(
    () =>
      startModeTransition(() => {
        void setMode('curate');
      }),
    [setMode],
  );
  // Drag-and-drop from map pin → Scouted drawer. The ScoutedList drop
  // target only has the item id; we look it up in the live search
  // results here (App.tsx owns `items`) and call addPlace. Default
  // place type is `activity` — matches AddToTripButton's default and
  // is the most-common case. The user can re-categorize in Curate.
  //
  // `items` is read via a ref so the callback ref stays stable when
  // search results refresh. Without this, the callback re-created on
  // every items change, defeating React.memo on ScoutedList and forcing
  // the Scout drawer to re-render on every place-search update.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const handlePlaceDropped = useCallback(
    (itemId: string) => {
      if (!selectedTripId) return;
      const item = itemsRef.current.find((i) => i.id === itemId);
      if (!item) return;
      addPlace(selectedTripId, item, 'activity');
    },
    [selectedTripId, addPlace],
  );

  // Map right-click / long-press handler. Opens the custom-pin modal
  // seeded with the clicked coords. When no trip is selected, we drop
  // the gesture rather than open the modal — the modal needs a trip
  // to add the pin to. (Possible future improvement: open the trip
  // selector first; for now we just no-op so the gesture doesn't
  // surface a modal with a disabled submit button.)
  const handleMapContextMenu = useCallback(
    (coords: { latitude: number; longitude: number }) => {
      if (!selectedTripId) return;
      setCustomPinDraft(coords);
    },
    [selectedTripId],
  );

  const filteredItems = useMemo(() => {
    const exclusions = customExclusions.exclusions;
    const hasCategoryFilter = selectedCategories.length > 0;
    if (!hasCategoryFilter && exclusions.length === 0) return items;
    return items.filter((item) => {
      if (hasCategoryFilter && !selectedCategories.includes(item.appCategory)) return false;
      if (matchesExclusion(item.name, exclusions)) return false;
      return true;
    });
  }, [items, selectedCategories, customExclusions.exclusions]);

  // The selected item might come from either the live search results OR the
  // active trip's saved places (e.g., clicking a row in the scouted list).
  // Look in both so the detail panel works regardless of source.
  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    const fromResults = items.find((item) => item.id === selectedItemId);
    if (fromResults) return fromResults;
    const fromTrip = selectedTripId
      ? trips.find((t) => t.id === selectedTripId)?.places.find((p) => p.item.id === selectedItemId)
          ?.item
      : null;
    return fromTrip ?? null;
  }, [items, selectedItemId, selectedTripId, trips]);

  const availableCategories = useMemo(
    () => [...new Set(items.map((item) => item.appCategory))] as Array<AppCategory>,
    [items],
  );

  const selectedTrip = useMemo(
    () => trips.find((t) => t.id === selectedTripId) ?? null,
    [trips, selectedTripId],
  );

  // Map-filter toggles. Declared here (rather than further down with
  // other UI state) so the travelDayTrip useMemo below can read
  // `tripPlacesTodayOnly` without a temporal-dead-zone error.
  const [tripPlacesOnly, setTripPlacesOnly] = useState(false);
  // Travel-mode-only sub-toggle: when on AND tripPlacesOnly is on,
  // further filter trip places to the active day's scheduled items.
  // Unscheduled places (e.g. lodging) always show every day so the
  // user keeps the "home base" markers in view as they navigate the
  // day pager. Defaults to ON so the existing Travel-mode behavior is
  // preserved on first paint.
  const [tripPlacesTodayOnly, setTripPlacesTodayOnly] = useState(true);

  // In travel mode, narrow the trip's places to (a) anything unscheduled and (b) anything
  // scheduled for the active day. Routes only connect the day's scheduled items; unscheduled
  // markers ride along so the user can still see + pick from them. Gated by the
  // `tripPlacesTodayOnly` toggle — when off, we just hand back the trip untouched so
  // the user sees every place on the map regardless of day.
  const travelDayTrip = useMemo<Trip | null>(() => {
    if (mode !== 'travel' || !selectedTrip || activeDay == null) return null;
    if (!tripPlacesTodayOnly) return selectedTrip;
    return {
      ...selectedTrip,
      places: selectedTrip.places.filter((p) => {
        if (p.scheduledDay == null) return true; // include all unscheduled
        const end = p.scheduledDayEnd ?? p.scheduledDay;
        return p.scheduledDay <= activeDay && activeDay <= end;
      }),
    };
  }, [mode, selectedTrip, activeDay, tripPlacesTodayOnly]);

  // Import a live-synced trip from the URL on first load via the persistent
  // `?live=<uuid>` link, then subscribe to ongoing updates over WebSocket
  // (managed inside useTrips). Access is gated server-side (`authorizeTrip`):
  // only the owner or a collaborator added by email can open + edit; joiner
  // devices push their own edits and apply incoming ops via per-field LWW.
  useEffect(() => {
    if (!liveJoinId) return;
    void setLiveJoinId(null);
    importLiveSyncedTrip(liveJoinId).then((trip) => {
      if (!trip) return;
      selectTrip(trip.id);
      openPanel();
      if (trip.lat != null && trip.lng != null) {
        void setLat(trip.lat);
        void setLng(trip.lng);
      }
      // Mode is handled by the trip↔mode coupling effect below (selecting a
      // trip from Explore lands in Scout).
    });
  }, [liveJoinId, setLng, openPanel, setLiveJoinId, setLat, selectTrip, importLiveSyncedTrip]); // eslint-disable-line react-hooks/exhaustive-deps — intentionally run once on mount

  // Trip and mode are coupled:
  //   - no trip + (scout|curate|travel) → fall back to explore
  //   - trip selected + explore → shift to scout (natural starting mode
  //     after picking a trip — scouting is "now collect things")
  //   - EXCEPT when the selected trip is live-synced → shift to travel
  //     instead. Receiving a live link is a "here's where we're at"
  //     hand-off; Travel (day-by-day, focused) matches that intent
  //     better than Scout (gathering more places).
  // Wrapped in startModeTransition so this auto-switch gets the same
  // non-blocking, progress-bar-visible treatment as a direct menu click.
  useEffect(() => {
    if (!selectedTripId && mode !== 'explore') {
      startModeTransition(() => {
        void setMode('explore');
      });
    } else if (selectedTripId && mode === 'explore') {
      // Selecting a trip from Explore lands in Scout — the planning workspace.
      startModeTransition(() => {
        void setMode('scout');
      });
    }
  }, [selectedTripId, mode, setMode]);

  // When a trip is selected, if today falls within the trip's date range auto-select that day
  // in the panel. Only fires on selectedTripId change so we don't override user navigation
  // when unrelated trip data updates. The closure over `trips` is intentional — we only need
  // start/end dates, which don't change as places are added/removed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally fires only on selectedTripId change; see comment above
  useEffect(() => {
    if (!selectedTripId) return;
    const trip = trips.find((t) => t.id === selectedTripId);
    if (!trip) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(`${trip.startDate}T00:00:00`);
    const end = new Date(`${trip.endDate}T00:00:00`);
    if (today >= start && today <= end) {
      const day = Math.floor((today.getTime() - start.getTime()) / 86_400_000) + 1;
      void setActiveDay(day);
    }
  }, [selectedTripId, setActiveDay]);

  // Fly map to the trip's stored centerpoint when the user selects a
  // trip. Intentionally keyed on `selectedTripId` only — depending on
  // `selectedTrip` would re-fire on every trip mutation (adding a
  // place, etc.) and yank the map back to the trip center every time.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate — see comment
  useEffect(() => {
    if (!selectedTripId) return;
    const trip = trips.find((t) => t.id === selectedTripId);
    if (trip?.lat == null || trip?.lng == null) return;
    void setLat(trip.lat);
    void setLng(trip.lng);
    // Restore the radius captured at creation so the trip's places search
    // reuses the same (center, radius) — and cache entry — that warmed the
    // area. Legacy trips predate `radius`; leave the current radius as-is.
    if (trip.radius != null) void setRadius(trip.radius);
  }, [selectedTripId, setLat, setLng, setRadius]);

  const hasSearched = lat !== null && lng !== null;
  const [isListCollapsed, setIsListCollapsed] = useState(false);
  const mapItems = tripPlacesOnly && scoutMode && selectedTrip ? [] : filteredItems;

  const [isTutorialOpen, setIsTutorialOpen] = useState(() => {
    try {
      return localStorage.getItem('trip-explorer:tour-seen') !== 'true';
    } catch {
      return false;
    }
  });

  const closeTutorial = () => {
    setIsTutorialOpen(false);
    try {
      localStorage.setItem('trip-explorer:tour-seen', 'true');
    } catch {
      // storage unavailable — tutorial will reopen next visit
    }
  };

  return (
    // `h-dvh` follows the dynamic viewport (iOS Safari url-bar collapse), so
    // the app never overflows the visible viewport. The kawaii bg is set on
    // body in theme.css so iOS safe-area regions render in the right color.
    <div className="flex flex-col h-dvh gradient-kawaii overflow-hidden">
      {/* Header. Safe-area-inset-top padding pushes content below the iOS
          status bar; the gradient still extends into the safe-area because
          the chrome above sees this element's background color via the body. */}
      <header
        className="relative flex-none z-navigation shadow-lg gradient-kawaii rounded-none"
        // env(safe-area-inset-top) handles the notch / Dynamic Island in
        // standalone PWA mode (and on iOS Safari 18+ with viewport-fit
        // = cover). The gradient bg extends INTO this padded area since
        // padding is inside the element — so the safe area itself is
        // tinted gradient, not the page's body bg.
        // Horizontal insets cover landscape orientations where the
        // notch eats the left or right edge.
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        {/* On <md viewports stack the search/trip chip above the actions
            chip so neither runs out of horizontal room. The desktop layout
            (wordmark + center chip + actions on one row) returns at md+. */}
        {/* Header zones stack vertically only on narrow phones; from `sm`
            (480px+) upward they sit on one row so wide phones + small
            tablets don't waste the available horizontal width. */}
        <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          {/* Full wordmark (md+): the frosted-chip brand mark, shared with the
              auth screens. Hidden below md, where the compact mark below takes
              over. */}
          <BrandWordmark className="hidden md:inline-flex" />
          {/* Compact small-screen brand mark: party-horn + tiny "T P"
              letters in the same palette as the full wordmark. Visible
              from sm onward (where the header lays out in a row) and
              hides at md+ where the full wordmark takes over. */}
          <h1
            className="font-diary font-semibold whitespace-nowrap hidden sm:inline-flex md:hidden items-center gap-1.5 shrink-0 bg-secondary-focal/60 backdrop-blur-md border border-white/15 rounded-xl px-2 h-10 overflow-hidden"
            aria-label="Trippy Plannny"
          >
            <FontAwesomeIcon
              icon={faPartyHorn}
              style={
                {
                  width: 18,
                  height: 18,
                  '--fa-primary-color': '#ffffff',
                  '--fa-secondary-color': '#ffc7da',
                  '--fa-secondary-opacity': '1',
                } as FAIconStyle
              }
            />
            <span className="font-diary text-base leading-none" aria-hidden>
              <span className="font-diary" style={{ color: '#f0b0e0' }}>
                T
              </span>
              <span className="font-diary" style={{ color: '#dac4f5' }}>
                P
              </span>
            </span>
            <FontAwesomeIcon
              icon={faPinata}
              style={
                {
                  width: 18,
                  height: 18,
                  '--fa-primary-color': '#ffffff',
                  '--fa-secondary-color': '#d9c5ff',
                  '--fa-secondary-opacity': '1',
                } as FAIconStyle
              }
            />
          </h1>
          {/* Middle zone — two layouts that align with the other header
              chips visually. SearchBar gets wrapped in a frosted chip (so
              the white pill input sits inside a uniform dark surface);
              TripHeader is self-chipped (its own translucent frosted
              surface stretches the full width without nested chip nesting,
              matching what felt right pre-chip). */}
          {mode === 'explore' || !selectedTrip ? (
            // Search-bar chip — symmetric p-1 padding all sides so the
            // wrapped white input has visually equal breathing room on
            // horizontal + vertical axes.
            <div className="flex-1 min-w-0 sm:mx-4 flex items-center bg-secondary-focal/60 backdrop-blur-md border border-white/15 rounded-xl p-1 overflow-hidden">
              <SearchBar
                className="w-full"
                disabled={!!selectedTripId}
                onLocationSelected={handleSearchBarLocation}
              />
            </div>
          ) : (
            <TripHeader className="flex-1 min-w-0 sm:mx-4" trip={selectedTrip} />
          )}
          {/* Right chip — wraps the tutorial / refresh / trip-mode-menu
              controls so all three header zones share the same frosted
              chip treatment for visual cohesion. Centered on the stacked
              (<md) layout so it doesn't hug the right edge alone. */}
          {/* Right action chip — symmetric p-1 padding so the wrapping
              around inner items (IconButtons, trip-menu pill) reads as
              visually equal on all four sides. */}
          <div className="shrink-0 self-center sm:self-auto flex items-center gap-0 bg-secondary-focal/60 backdrop-blur-md border border-white/15 rounded-xl p-1">
            {/* Account menu — discloses admin + sign-out behind a user icon,
                left of the help control. */}
            <UserMenu
              isAdmin={isAdmin}
              onOpenAdmin={() => {
                window.location.href = '/admin';
              }}
              onSignOut={() => {
                void signOut().then(() => window.location.reload());
              }}
            />
            <IconButton
              icon={faCircleQuestion}
              ariaLabel="Open tutorial"
              tone="inverse"
              shape="circle"
              size="base"
              iconSize={18}
              duotoneVariant="inverse"
              onClick={() => setIsTutorialOpen(true)}
            />
            {/* Filters trigger — compact icon+radius button next to refresh.
                Only appears in modes that work off the live places search
                (explore + scout). Tapping opens the same popover the wide
                filter chip used to. */}
            {showFilterRow && (
              <FiltersPopover
                availableCategories={availableCategories}
                enabledCategoryCount={selection.enabled.size}
                onOpenCategoryCustomization={handleOpenCategoryCustomization}
              />
            )}
            {/* Refresh is meaningful in Explore + Scout (both modes hit the
                live places search). Hidden in Curate/Travel where the trip's
                saved places are the source of truth. */}
            {showFilterRow && (
              <IconButton
                icon={faArrowsRotate}
                ariaLabel="Refresh place data"
                title="Refresh places"
                tone="inverse"
                shape="circle"
                size="base"
                iconSize={16}
                duotoneVariant="inverse"
                disabled={!hasSearched}
                onClick={() => setIsRefreshModalOpen(true)}
              />
            )}
            <TripModeMenu
              mode={mode}
              onModeChange={handleModeChange}
              showRoutes={showRoutes}
              onShowRoutesChange={handleShowRoutesChange}
              tripPlacesOnly={tripPlacesOnly}
              onTripPlacesOnlyChange={setTripPlacesOnly}
              tripPlacesTodayOnly={tripPlacesTodayOnly}
              onTripPlacesTodayOnlyChange={setTripPlacesTodayOnly}
              onShowInstallInstructions={handleOpenInstallInstructions}
            />
          </div>
        </div>

        {/* Filters moved into the right-chip as a compact icon+radius button;
            the wide filter chip row is gone. Curate's mode badge floats over
            the header/content threshold via the absolute-positioned element
            below. */}
        {curateMode && (
          // Floats just below the header's bottom edge so it doesn't sit on
          // top of header controls. Scales down on mobile so it fits the
          // narrower viewport. Pointer-events-none keeps it visual-only.
          <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-[60%] pointer-events-none z-content-overlay origin-top scale-[0.65] sm:scale-100">
            <ModeBubble label={modeLabels[mode]} />
          </div>
        )}
      </header>

      {/* Main content */}
      <main
        // `relative` so the Travel-mode slide-over panel + scrim (below)
        // can position themselves as `absolute inset-0` against `main`
        // instead of `fixed`. Using `fixed` made the slide-over escape
        // the layout and cover the header — on narrow phones where the
        // panel is `w-screen` that hid the trip name + mode menu
        // entirely. Anchoring to `main` keeps the header visible.
        className="flex-1 flex flex-col md:flex-row overflow-hidden bg-kawaii relative"
        // env(safe-area-inset-bottom) reserves space for the iOS home
        // indicator (or any future bottom-anchored OS chrome). Padding
        // is INSIDE main, so the bg-kawaii (pale lavender) extends into
        // the indicator area — the home indicator sits against the
        // pale bg, not against arbitrary content. Horizontal insets
        // for landscape, same idea as the header.
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        {curateMode && selectedTrip ? (
          <div className="flex-1 flex overflow-hidden">
            <TripPlanningView
              key={selectedTrip.id}
              trip={selectedTrip}
              leftPaneOverride={
                selectedItem ? (
                  <PlaceDetailPanel
                    item={selectedItem}
                    onClose={clearSelectedItem}
                    variant="inline"
                    closeLabel="Back to Unscheduled"
                  />
                ) : undefined
              }
            />
          </div>
        ) : travelMode && selectedTrip ? (
          <>
            {/* Map fills the area; only trip places for the active day are shown */}
            <div className="flex-1 order-1 md:order-1 relative">
              {selectedTrip.lat != null && selectedTrip.lng != null ? (
                <ItineraryMap
                  items={[]}
                  allItems={[]}
                  lat={selectedTrip.lat}
                  lng={selectedTrip.lng}
                  radiusMiles={radius}
                  selectedTrip={travelDayTrip ?? selectedTrip}
                  showRoutes={showRoutes}
                  travelMode={true}
                  onMapContextMenu={handleMapContextMenu}
                />
              ) : (
                <MapPlaceholder />
              )}
              {/* Mode badge — floats over the top edge of the map. Hidden when
                  the placeholder is showing so the "Traveling" bubble doesn't
                  read as live UI when the trip has no location. */}
              {selectedTrip.lat != null && selectedTrip.lng != null && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-content-overlay pointer-events-none origin-top scale-[0.65] sm:scale-100">
                  <ModeBubble label={modeLabels[mode]} />
                </div>
              )}
              {/* Mobile-only grab handle to re-open the trip panel after the
                  user dismisses it (tapping outside the slide-over closes it
                  via the scrim). Same visual treatment as the Scout
                  list-collapse handle, anchored to the right edge of the map
                  so the affordance reads as "drag a panel in". */}
              {!(isPanelOpen || selectedItem) && (
                <PanelCollapseHandle
                  side="right"
                  isCollapsed={true}
                  onClick={openPanel}
                  ariaLabel="Open trip panel"
                  className="md:hidden"
                />
              )}
            </div>
            {/* No scrim — the panel's own back-to-map button is the close
                affordance, on both small (full-width drawer) and medium
                (right-docked) screens. A scrim here was confusing because
                it implied "dim the rest" but the only way to close the
                drawer is the in-panel button anyway. */}
            {/* Right pane — schedule (or place detail when one is selected).
                On mobile it's a fixed slide-over gated by `isPanelOpen`;
                desktop always renders it inline next to the map at a fixed
                width so place-detail vs trip-panel don't shift the layout. */}
            <div
              className={twx(
                'order-2 md:relative md:inset-auto md:z-auto md:flex md:w-[400px]',
                // Mobile open: full-viewport-width on narrow phones (so
                // there's no thin underlying-chrome strip on either side),
                // capped at 420px from `sm` upward. Anchored to `main`
                // via `absolute` (not `fixed`) so the header above stays
                // visible while the drawer is open. Mirrors the
                // SlideOverSheet contract used by Scout / Explore so all
                // trip-side panels feel uniform.
                isPanelOpen || selectedItem
                  ? 'absolute inset-y-0 right-0 w-full sm:w-[420px] z-modal flex'
                  : 'hidden',
              )}
            >
              {selectedItem ? (
                <PlaceDetailPanel
                  item={selectedItem}
                  onClose={clearSelectedItem}
                  variant="inline"
                  closeLabel="Back to trip"
                />
              ) : (
                <TripPanel onClose={closePanel} lockTripMeta={true} lockScheduling={true} />
              )}
            </div>
          </>
        ) : (
          <>
            {/* List / Detail panel — bottom on mobile, left column on desktop.
                Always visible on mobile so the pre-search empty state shows
                as a "what now?" bottom panel instead of being hidden behind
                the decorative map placeholder.

                In Scout mode with a selected trip, the small-screen panel
                gets a Tabs bar at the top: tab 1 swaps in the places list,
                tab 2 swaps in the ScoutedList. On desktop the scouted list
                sits in its own inline column (see SlideOverSheet below), so
                tabs only render below md. */}
            <div
              className={`md:shrink-0 flex-1 md:flex-none min-h-0 md:min-h-full order-2 md:order-1 overflow-hidden border-t md:border-t-0 md:border-r border-content-divider relative transition-all duration-200 flex flex-col ${isListCollapsed ? 'md:w-0 md:border-r-0' : 'md:w-[400px]'}`}
            >
              {scoutMode && selectedTrip && !selectedItem && (
                <div className="md:hidden shrink-0">
                  <Tabs
                    options={[
                      { value: 'places', label: 'Places' },
                      {
                        value: 'scouted',
                        // Matches the "SCOUTED" eyebrow inside ScoutedList
                        // and the Scouting mode bubble.
                        label: 'Scouted',
                        // Count = unscheduled places (the ones actually in
                        // the "Scouted" bucket, not already-curated places
                        // that render as faded references underneath).
                        count: selectedTrip.places.filter((p) => p.scheduledDay == null).length,
                      },
                    ]}
                    value={scoutTab}
                    // Tab switching wraps in startTransition so the URL
                    // + state update happens at non-urgent priority —
                    // keeps the tap visually-responsive even if the
                    // newly-shown panel needs to do any commit work.
                    onChange={(next) =>
                      startModeTransition(() => {
                        void setScoutTab(next);
                      })
                    }
                  />
                </div>
              )}
              {/* Always keep the list mounted to avoid remount cost when closing detail */}
              <div
                className={twx(
                  'flex-1 min-h-0',
                  selectedItem && 'hidden',
                  // On small screens in Scout mode, hide the list when the
                  // Scouted tab is active. Always-visible on desktop.
                  scoutMode && selectedTrip && scoutTab === 'scouted' && 'hidden md:block',
                )}
              >
                <ItineraryList
                  items={filteredItems}
                  isLoading={isLoading}
                  hasSearched={hasSearched}
                />
              </div>
              {/* Small-screen-only Scouted tab content. Desktop renders the
                  scouted list in its own inline column via the SlideOverSheet
                  below. Stays mounted whenever scout+selectedTrip+no-detail —
                  visibility flips via `hidden` instead of unmount/remount
                  on each tab switch (same pattern as ItineraryList above).
                  Without this every tab toggle re-mounted the whole per-day
                  timeline subtree. */}
              {scoutMode && selectedTrip && !selectedItem && (
                <div
                  className={twx(
                    'md:hidden flex-1 min-h-0 flex',
                    scoutTab !== 'scouted' && 'hidden',
                  )}
                >
                  <ScoutedList
                    trip={selectedTrip}
                    onCurate={handleOpenCurate}
                    onPlaceDropped={handlePlaceDropped}
                  />
                </div>
              )}
              {selectedItem && (
                <div className="absolute inset-0">
                  <PlaceDetailPanel
                    item={selectedItem}
                    onClose={clearSelectedItem}
                    variant="inline"
                  />
                </div>
              )}
            </div>

            {/* Map panel — top on mobile, flex-1 on desktop. Fills full height when list is hidden. */}
            <div
              className={`md:h-auto md:flex-1 order-1 md:order-2 relative shrink-0 md:shrink ${!(hasSearched || isLoading || selectedItem) ? 'flex-1' : 'h-[34vh]'}`}
            >
              {/* Collapse toggle — desktop only, shown when there are results.
                  Uses the shared PanelCollapseHandle primitive (the same
                  tab style as the mobile "Open trip panel" thumb). */}
              {(hasSearched || isLoading) && (
                <PanelCollapseHandle
                  side="left"
                  isCollapsed={isListCollapsed}
                  onClick={() => setIsListCollapsed((prev) => !prev)}
                  ariaLabel={isListCollapsed ? 'Expand list panel' : 'Collapse list panel'}
                  className="hidden md:flex"
                />
              )}
              {hasSearched && lat !== null && lng !== null ? (
                <ItineraryMap
                  items={mapItems}
                  allItems={items}
                  lat={lat}
                  lng={lng}
                  radiusMiles={radius}
                  selectedTrip={selectedTrip}
                  showRoutes={showRoutes}
                  // The chip stays in its "Finding places…" state through the
                  // whole background fill — initial fetch AND the silent
                  // pending-poll window (ingest + photo enrichment).
                  isLoading={isLoading || isSettling}
                  // Enable drag in Scout mode only — that's the only mode
                  // where the ScoutedList drop target is visible. The map
                  // itself doesn't render the drop target; it lets users
                  // pick up a pin and drop it on the Scouted area.
                  enableDrag={scoutMode && selectedTrip != null}
                  onMapContextMenu={handleMapContextMenu}
                />
              ) : (
                <MapPlaceholder />
              )}
              {/* Mode badge — floats over the top edge of the map. Hidden when
                  the placeholder is showing, so the "Exploring" bubble doesn't
                  read as live UI before the user has searched. */}
              {hasSearched && lat !== null && lng !== null && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-content-overlay pointer-events-none origin-top scale-[0.65] sm:scale-100">
                  <ModeBubble label={modeLabels[mode]} />
                </div>
              )}
            </div>

            {/* Scout mode: scouted-list inline-column on desktop only. The
                small-screen treatment moved to a Tabs slot in the bottom
                panel above (look for `scoutTab === 'scouted'`). We force
                `isOpen={false}` here so SlideOverSheet emits only the
                desktop inline column and never the mobile overlay. */}
            {scoutMode && selectedTrip && (
              <SlideOverSheet
                isOpen={false}
                onClose={closePanel}
                scrimAriaLabel="Close scouted list"
                testId="scouted-list"
              >
                <ScoutedList
                  trip={selectedTrip}
                  onCurate={handleOpenCurate}
                  onPlaceDropped={handlePlaceDropped}
                />
              </SlideOverSheet>
            )}

            {/* Explore mode: trip panel only renders when the user opens it. */}
            {!scoutMode && (
              <SlideOverSheet
                isOpen={isPanelOpen}
                onClose={closePanel}
                desktopGated
                scrimAriaLabel="Close trip panel"
                testId="explore-trip-panel"
              >
                <TripPanel onClose={closePanel} />
              </SlideOverSheet>
            )}
          </>
        )}
      </main>
      <ModeSwitchProgress mode={mode} isPending={isModeSwitchPending} />
      <TutorialModal isOpen={isTutorialOpen} onClose={closeTutorial} />
      <CategoryCustomizationModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
        groups={categoryGroups.groups}
        isLoading={categoryGroups.isLoading}
        error={categoryGroups.error}
        selection={selection}
      />
      <RefreshConfirmationModal
        isOpen={isRefreshModalOpen}
        onClose={handleCloseRefreshModal}
        onConfirm={handleRefreshArea}
      />
      <InstallAppModal isOpen={isInstallModalOpen} onClose={handleCloseInstallModal} />
      <CreateCustomPinModal draft={customPinDraft} onClose={() => setCustomPinDraft(null)} />
      <CollaborateModal
        trip={trips.find((t) => t.id === collaborateForTripId) ?? null}
        onClose={() => setCollaborateForTripId(null)}
      />
    </div>
  );
};

export const App = () => (
  <TripProvider>
    <AppInner />
  </TripProvider>
);
